import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Alert, Button, message, Popconfirm, Space, Table, Tag } from "antd";
import {
  PauseCircleOutlined,
  PlayCircleOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { DisableForViewer } from "../../components/common/DisableForViewer";
import { LinkButton } from "../../components/LinkButton";
import {
  FETCH_PAGE_SIZE,
  useCursorPagination,
} from "../../hooks/useCursorPagination";
import {
  listEmulatedSites,
  pauseEmulatedSite,
  resetEmulatedSite,
  unpauseEmulatedSite,
  type EmulatedSite,
} from "../../services/emulator/api";
import { formatDateTime } from "../../util/formatDate";

export const EmulatorListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["emulatedSites", { page_size: FETCH_PAGE_SIZE }],
    queryFn: ({ pageParam }) =>
      listEmulatedSites({ page_size: FETCH_PAGE_SIZE, after: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.end_cursor,
  });

  const pauseMutation = useMutation({
    mutationFn: ({ siteId, paused }: { siteId: string; paused: boolean }) =>
      paused ? unpauseEmulatedSite(siteId) : pauseEmulatedSite(siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emulatedSites"] });
    },
    onError: (error: Error) => {
      message.error(error.message || "Failed to update site status");
    },
  });

  const resetMutation = useMutation({
    mutationFn: (siteId: string) => resetEmulatedSite(siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emulatedSites"] });
      message.success("Site has been reset");
    },
    onError: (error: Error) => {
      message.error(error.message || "Failed to reset site");
    },
  });

  const { dataSource, pagination, isPageLoading } =
    useCursorPagination<EmulatedSite>({
      pages: data?.pages,
      hasNextPage: hasNextPage ?? false,
      fetchNextPage,
      isFetchingNextPage,
    });

  const columns: ColumnsType<EmulatedSite> = [
    { title: "Site ID", dataIndex: "site_id", key: "site_id" },
    { title: "Partner ID", dataIndex: "partner_id", key: "partner_id" },
    {
      title: "OCPP Config",
      dataIndex: "ocpp_config",
      key: "ocpp_config",
      render: (config: EmulatedSite["ocpp_config"]) => (
        <Tag color={config ? "green" : "default"}>
          {config ? "Configured" : "None"}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "paused",
      key: "paused",
      render: (paused: boolean, record) => (
        <Space>
          <Tag color={paused ? "red" : "green"}>
            {paused ? "Paused" : "Running"}
          </Tag>
          <DisableForViewer>
            <Button
              type="default"
              icon={paused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                pauseMutation.mutate({ siteId: record.site_id, paused });
              }}
              loading={
                pauseMutation.isPending &&
                pauseMutation.variables?.siteId === record.site_id
              }
            >
              {paused ? "Unpause" : "Pause"}
            </Button>
          </DisableForViewer>
        </Space>
      ),
    },
    {
      title: "Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (val?: string | null) => (val ? formatDateTime(val) : "N/A"),
    },
  ];

  return (
    <>
      {error && (
        <Alert
          message="Error"
          description="There was an issue loading the emulated sites."
          type="error"
          showIcon
        />
      )}
      <Table<EmulatedSite>
        columns={[
          ...columns,
          {
            title: "",
            key: "actions",
            render: (_, record) => (
              <Space>
                <LinkButton
                  to={`/emulator/${record.site_id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  Start transaction
                </LinkButton>
                <Popconfirm
                  title="Reset site"
                  description="This will clear all simulator state and reinitialize the site. Continue?"
                  onConfirm={(e) => {
                    e?.stopPropagation();
                    resetMutation.mutate(record.site_id);
                  }}
                  onCancel={(e) => e?.stopPropagation()}
                  okText="Reset"
                  cancelText="Cancel"
                >
                  <DisableForViewer>
                    <Button
                      type="default"
                      icon={<UndoOutlined />}
                      onClick={(e) => e.stopPropagation()}
                      loading={
                        resetMutation.isPending &&
                        resetMutation.variables === record.site_id
                      }
                    >
                      Reset
                    </Button>
                  </DisableForViewer>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
        dataSource={dataSource}
        loading={isLoading || isPageLoading}
        pagination={pagination}
        onRow={(record) => ({
          onClick: () => navigate(`/emulator/${record.site_id}`),
        })}
        rowKey="site_id"
      />
    </>
  );
};

export default EmulatorListPage;
